import React, { useState, useEffect } from 'react';
import {
  Table,
  Typography,
  Space,
  Button,
  Tag,
  Card,
  Empty,
  Spin,
  message,
} from 'antd';
import { DownloadOutlined, DatabaseOutlined } from '@ant-design/icons';
import { getBackups, getBackupByDate } from '../api/client';
import { accountLabel } from '../utils/accountNames';

const { Title, Text } = Typography;

// ─── Backups Component ────────────────────────────────────────────────────────

const Backups = () => {
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState([]);

  // Stores per-date details as they're lazy-loaded when the user expands a row.
  // Shape: { [date]: { loading: bool, data: [...] | null } }
  const [expandedDetails, setExpandedDetails] = useState({});

  // ── Fetch the list of backup dates on mount ─────────────────────────────────

  useEffect(() => {
    const fetchBackups = async () => {
      setLoading(true);
      try {
        const data = await getBackups();
        setBackups(data.backups ?? data ?? []);
      } catch (err) {
        message.error('Failed to load backup list.');
      } finally {
        setLoading(false);
      }
    };
    fetchBackups();
  }, []);

  // ── Lazy-load per-SG details when a row is expanded ─────────────────────────
  //
  // We only fetch a date's details once — the data is cached in `expandedDetails`.
  //
  const loadDateDetails = async (date) => {
    if (expandedDetails[date]) {
      return; // Already loaded (or loading), don't fetch again
    }

    setExpandedDetails((prev) => ({
      ...prev,
      [date]: { loading: true, data: null },
    }));

    try {
      const data = await getBackupByDate(date);
      setExpandedDetails((prev) => ({
        ...prev,
        [date]: {
          loading: false,
          data: data.sgs ?? [],
        },
      }));
    } catch (err) {
      message.error(`Failed to load backup details for ${date}.`);
      setExpandedDetails((prev) => ({
        ...prev,
        [date]: { loading: false, data: [] },
      }));
    }
  };

  // ── Inner table shown inside an expanded row ────────────────────────────────
  //
  // Lists each Security Group that was backed up on that date,
  // with the rule count and a download link for the JSON backup file.
  //
  const expandedRowRender = (record) => {
    const detail = expandedDetails[record.date];

    if (!detail || detail.loading) {
      return (
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: 8 }}>
            Loading details...
          </Text>
        </div>
      );
    }

    if (!detail.data || detail.data.length === 0) {
      return <Empty description="No security group details available for this backup." />;
    }

    const innerColumns = [
      {
        title: 'Security Group',
        key: 'sg',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Text strong>{row.sg_name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.sg_id}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Account',
        dataIndex: 'account_id',
        key: 'account_id',
        render: (id) => <Text code style={{ fontSize: 12 }}>{accountLabel(id)}</Text>,
      },
      {
        title: 'Region',
        dataIndex: 'region',
        key: 'region',
      },
      {
        title: 'Rules Backed Up',
        dataIndex: 'rule_count',
        key: 'rule_count',
        align: 'right',
        render: (count) => (
          <Tag color="blue">{count ?? 0} rules</Tag>
        ),
      },
      {
        title: 'Download',
        key: 'download',
        render: (_, row) =>
          row.download_url ? (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              href={row.download_url}
              target="_blank"
              rel="noopener noreferrer"
              // download_url is a pre-signed S3 URL — opens directly in a new tab
            >
              Download JSON
            </Button>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
    ];

    return (
      <Table
        dataSource={detail.data}
        columns={innerColumns}
        rowKey="sg_id"
        pagination={false}
        size="small"
        style={{ margin: '8px 0' }}
      />
    );
  };

  // ── Outer table columns (one row per backup date) ───────────────────────────

  const outerColumns = [
    {
      title: 'Backup Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => b.date.localeCompare(a.date), // newest first by default
      defaultSortOrder: 'ascend', // combined with the sorter above = newest first
      render: (date) => (
        <Space>
          <DatabaseOutlined style={{ color: '#1890ff' }} />
          <Text strong>{date}</Text>
        </Space>
      ),
    },
    {
      title: 'Security Groups Backed Up',
      dataIndex: 'sg_count',
      key: 'sg_count',
      align: 'right',
      render: (count) => <Tag color="blue">{count ?? 0} SGs</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const normalised = (status ?? 'complete').toLowerCase();
        return (
          <Tag color={normalised === 'complete' ? 'success' : 'warning'}>
            {normalised}
          </Tag>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (ts) =>
        ts ? (
          <Text style={{ fontSize: 13 }}>{new Date(ts).toLocaleString()}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>

      {/* ── Page Header ────────────────────────────────────────────────── */}
      <div>
        <Title level={4} style={{ margin: 0 }}>
          Security Group Backups
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Daily snapshots of all Security Group rules. Click a row to see per-SG
          details and download links.
        </Text>
      </div>

      {/* ── Backups Table ──────────────────────────────────────────────── */}
      <Card title="Available Backups">
        <Table
          dataSource={backups}
          columns={outerColumns}
          rowKey="date"
          loading={loading}
          expandable={{
            expandedRowRender,
            // When a row is expanded, start loading its details
            onExpand: (expanded, record) => {
              if (expanded) {
                loadDateDetails(record.date);
              }
            },
          }}
          pagination={{
            pageSize: 30,
            showTotal: (total) => `${total} backup${total !== 1 ? 's' : ''}`,
          }}
          size="small"
          locale={{
            emptyText: loading ? (
              'Loading...'
            ) : (
              <Empty description="No backups available yet. Backups are generated daily." />
            ),
          }}
        />
      </Card>

    </Space>
  );
};

export default Backups;
